import React, { FC, useState } from "react";
import styled from "styled-components";
import Countdown, { zeroPad } from "react-countdown-now";
import Modal from "react-responsive-modal";
import gql from "graphql-tag";
import dayjs from "dayjs";
import { setCookie } from "nookies";
import { Formik } from "formik";
import { string, object } from "yup";
import { Mutation, useMutation } from "react-apollo";

import TextInput from "../fields/TextInput";
import Button from "../common/Button";
import Policy from "../common/Policy";
import {
  createCustomerAccessTokenByPhone,
  createCustomerAccessTokenByPhoneVariables,
} from "../../__generated__/createCustomerAccessTokenByPhone";
import { GREEN } from "../styles/constants";
import { GRAY } from "../styles/constants";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import {
  authByVoicePassword,
  authByVoicePasswordVariables,
} from "../../__generated__/authByVoicePassword";
import { authBySms, authBySmsVariables } from "../../__generated__/authBySms";

const GET_TOKEN = gql`
  mutation createCustomerAccessTokenByPhone(
    $phone: PhoneNumber!
    $codeOrPassword: String!
  ) {
    createCustomerAccessTokenByPhone(
      data: { phone: $phone, codeOrPassword: $codeOrPassword }
    ) {
      accessToken
      expiresAt
    }
  }
`;

const VOICE_PASSWORD_AUTH = gql`
  mutation authByVoicePassword($phone: PhoneNumber!, $token: String!) {
    authByVoicePassword(phone: $phone, token: $token)
  }
`;

const SMS_AUTH = gql`
  mutation authBySms($phone: PhoneNumber!, $token: String!) {
    authByFlashSms(phone: $phone, token: $token)
  }
`;

const AuthorizationForm: FC<{ show: boolean; setShow: any }> = ({
  show,
  setShow,
}) => {
  const [codeSent, setCodeSent] = useState(false);
  const [completedCount, setCompletedCount] = useState(false);
  const [smsMethod, setSmsMethod] = useState(false);
  const [phone, setPhone] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const { executeRecaptcha } = useGoogleReCaptcha();

  const [authByVoicePasswordMutation, { loading: voiceLoading }] = useMutation<
    authByVoicePassword,
    authByVoicePasswordVariables
  >(VOICE_PASSWORD_AUTH);

  const [authBySmsMutation, { loading: smsLoading }] = useMutation<
    authBySms,
    authBySmsVariables
  >(SMS_AUTH);

  const loading = voiceLoading || smsLoading;

  const renderer = ({ minutes, seconds, completed }) => {
    setCompletedCount(false);
    if (completed) {
      setCompletedCount(true);
      return <span>0</span>;
    } else {
      return (
        <span>
          {minutes !== 0 && `${minutes}:`}
          {zeroPad(seconds)}
        </span>
      );
    }
  };

  async function requestNewCode(phone: string) {
    const token = await executeRecaptcha("smsAuth");
    await authBySmsMutation({
      variables: {
        token,
        phone,
      },
    });
    setCompletedCount(false);
  }

  const count = <Countdown date={Date.now() + 70000} renderer={renderer} />;

  return (
    <Modal
      open={show}
      onClose={() => setShow(false)}
      classNames={{
        modal: "custom-modal-thanks-page",
        overlay: "",
        closeButton: "custom-close-white",
      }}
    >
      <Formik
        initialValues={{ telephone: "", code: "" }}
        onSubmit={(_, { setSubmitting }) => {
          setTimeout(() => {
            setSubmitting(false);
          }, 500);
        }}
        validationSchema={object().shape({
          telephone: string().required("Required"),
          code: string().required("Required"),
        })}
      >
        {(props) => {
          const {
            values,
            touched,
            errors,
            handleChange,
            handleBlur,
            handleSubmit,
          } = props;

          return (
            <Form onSubmit={handleSubmit}>
              {codeSent ? (
                <>
                  <>
                    <Digit className="kabel">{smsMethod ? "3" : "1"}</Digit>
                    <Header>
                      {smsMethod ? (
                        <>Введите код из&nbsp;СМС</>
                      ) : (
                        <>Примите звонок</>
                      )}
                    </Header>
                    <Text>
                      <p>
                        {smsMethod ? (
                          <>
                            Мы отправим вам СМС с кодом
                            <br /> подтверждения на номер
                          </>
                        ) : (
                          "Мы вам скоро позвоним на"
                        )}
                        <br />
                        {`${phone.replace(
                          /(\d{1})(\d{3})(\d{3})(\d{2})(\d{2})/,
                          "$1 $2 $3 $4 $5"
                        )}`}
                      </p>
                      <span
                        onClick={() => {
                          setCodeSent(false);
                        }}
                      >
                        Изменить номер телефона
                      </span>
                    </Text>
                    <Divider />
                  </>
                  {smsMethod ? null : (
                    <DigitLast>
                      <Digit className="kabel">2</Digit>
                      <h4 className="smallerH4">
                        Введите цифры, которые вам&nbsp;продиктуют
                      </h4>
                    </DigitLast>
                  )}
                </>
              ) : (
                <>
                  <Header>Авторизоваться</Header>

                  <Text>
                    <p>
                      Пожалуйста, введите номер своего телефона, чтобы
                      авторизоваться
                    </p>
                  </Text>

                  <Spacer />

                  <TextInput
                    {...props}
                    id="auth-telephone"
                    name="telephone"
                    value={values.telephone}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={(errors.code && touched.code) || !!errorMessage}
                    mask="+7 999-999-99-99"
                  />
                  <SubmitButton
                    type="button"
                    disabled={values.telephone ? false : true}
                    loading={loading}
                    onClick={async () => {
                      setPhone(values.telephone.replace(/[^+\d]/g, ""));

                      const token = await executeRecaptcha("smsAuth");

                      await authByVoicePasswordMutation({
                        variables: {
                          token,
                          phone: values.telephone.replace(/[^+\d]/g, ""),
                        },
                      });

                      setCodeSent(true);
                    }}
                    bold
                    primary
                    full
                    big
                  >
                    ПОЛУЧИТЬ КОД
                  </SubmitButton>
                </>
              )}

              {codeSent && (
                <Mutation<
                  createCustomerAccessTokenByPhone,
                  createCustomerAccessTokenByPhoneVariables
                >
                  mutation={GET_TOKEN}
                  onCompleted={(data) => {
                    const expireDate = dayjs(
                      data.createCustomerAccessTokenByPhone.expiresAt
                    ).diff(dayjs());
                    setCookie(
                      null,
                      "token",
                      data.createCustomerAccessTokenByPhone.accessToken,
                      {
                        path: "/",
                        maxAge: expireDate,
                      }
                    );
                    window.location.replace(`/profile`);
                  }}
                  onError={(err) => {
                    console.error(err);
                    setErrorMessage("Неверный код подтверждения.");
                  }}
                >
                  {(getToken, { loading: codeLoading }) => (
                    <>
                      <CustomTextInput
                        {...props}
                        id="auth-code"
                        name="code"
                        value={values.code}
                        onChange={(props) => {
                          handleChange(props);
                          setErrorMessage("");
                        }}
                        onBlur={handleBlur}
                        error={(errors.code && touched.code) || !!errorMessage}
                        errorMessage={errorMessage}
                        placeholder="Код подтверждения"
                        mask="9999"
                      />
                      <SubmitButton
                        type="submit"
                        disabled={values.code ? false : true}
                        loading={codeLoading}
                        onClick={() => {
                          getToken({
                            variables: {
                              phone,
                              codeOrPassword: values.code,
                            },
                          });
                        }}
                        bold
                        primary
                        full
                        big
                      >
                        ПОДТВЕРДИТЬ И ВОЙТИ
                      </SubmitButton>
                    </>
                  )}
                </Mutation>
              )}

              {codeSent ? (
                <Block>
                  {completedCount ? (
                    <Span
                      onClick={() => {
                        setSmsMethod(true);
                        requestNewCode(values.telephone.replace(/[^+\d]/g, ""));
                      }}
                    >
                      Получить новый код
                    </Span>
                  ) : (
                    <Repeat>Повторить звонок можно будет через {count}</Repeat>
                  )}
                </Block>
              ) : (
                <Policy center name="Получить код" showRecaptcha />
              )}
            </Form>
          );
        }}
      </Formik>
    </Modal>
  );
};

const Form = styled.form`
  background: #fff;
  border-radius: 15px;
  padding: 3.125rem 2.25rem 2.25rem;
  width: 365px;
  max-width: 100%;
  text-align: center;
  box-shadow: 22px 44px 44px rgba(0, 0, 0, 0.25);

  h4 {
    color: #000;
  }
`;

const SubmitButton = styled(Button)`
  margin: 0.75rem 0 1.25rem;
`;

const Header = styled.h4`
  margin: 0 0 0.25rem;
`;

const Text = styled.div`
  margin: 0.25rem 0 0.5rem;
  color: ${GRAY};
  font-size: 12px;
  line-height: 1.16;

  p {
    margin: 0 0 0.5rem;
  }

  span {
    color: ${GREEN};
    cursor: pointer;
  }
`;

const Block = styled.div`
  margin: 10px;
  margin-bottom: 0;
  font-size: 12px;
  color: #000;
`;

const Span = styled.span`
  color: ${GREEN};
  cursor: pointer;
`;

const CustomTextInput = styled(TextInput)`
  text-align: center;
`;

const DigitLast = styled.div`
  margin-bottom: 18px;
`;

const Digit = styled.div`
  width: 89px;
  height: 89px;
  margin: 0 auto -25px;
  background: linear-gradient(180deg, #f5f4f0 0%, rgba(245, 244, 240, 0) 100%);
  font-weight: 700;
  font-size: 56px;
  line-height: 89px;
  text-align: center;
  color: #dadada;
  border-radius: 50%;
`;

const Divider = styled.div`
  height: 14px;
`;

const Repeat = styled.p`
  opacity: 0.5;
  font-size: 12px;
`;

const Spacer = styled.span`
  display: block;
  height: 1.875rem;
`;

export default AuthorizationForm;
